/**
 * TRUS-734 — what counts as a member-portal invite, on both sides of the wire.
 *
 * The backend (`_is_member_invite` in app/routers/user_management.py) takes its
 * member branch — reuse the roster member, send the portal invite — when the
 * invitation's `role` is the bare 'member' (any case) OR is the role_type of an
 * active role_terminology row for the org whose system_role is 'member'. The
 * wizard sends role_type strings, never row ids. This module applies the same
 * rule so the existing-member picker shows exactly when that branch runs.
 *
 * No org has a member role row in production, so the wizard also offers a
 * built-in option whose value is the bare role the backend keys on.
 */

/** Select value AND the role string sent for the built-in Member option. */
export const MEMBER_ROLE_VALUE = 'member';

export interface InviteRoleOption {
  id: number | string;
  role_type: string;
  system_role?: string | null;
}

/** The role string the invite request sends for a wizard selection. */
export function resolveInviteRoleType(selected: string, orgRoles: InviteRoleOption[]): string {
  if (selected === MEMBER_ROLE_VALUE) return MEMBER_ROLE_VALUE;
  const role = orgRoles.find((r) => String(r.id) === selected);
  return role?.role_type || selected;
}

/** Mirror of the backend's `_is_member_invite`. */
export function isMemberInvite(selected: string, orgRoles: InviteRoleOption[]): boolean {
  const roleType = resolveInviteRoleType(selected, orgRoles).trim();
  if (!roleType) return false;
  if (roleType.toLowerCase() === MEMBER_ROLE_VALUE) return true;
  // Backend matches the org's role_terminology row by role_type.
  return orgRoles.some(
    (r) => r.role_type === roleType && (r.system_role || '').toLowerCase() === MEMBER_ROLE_VALUE,
  );
}
